// useAchievements: evaluates earned badges from current state, persists any
// newly-earned ones to `user_achievements`, and returns the list to render.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePebble } from './usePebble';
import { evaluateBadges, BADGES } from '@/lib/badges';
import { track } from '@/lib/analytics';
import { toast } from 'sonner';

export interface EarnedRow {
  badgeId: string;
  earnedAt: string;
}

export function useAchievements() {
  const store = usePebble();
  const loaded = store.isLoaded();
  const [persisted, setPersisted] = useState<EarnedRow[]>([]);
  const [persistedLoaded, setPersistedLoaded] = useState(false);
  const [accountCreatedAt, setAccountCreatedAt] = useState<string | null>(null);
  const userId = store.getUserId();

  // Load persisted achievements + account creation time once we have a user.
  useEffect(() => {
    if (!userId) {
      setPersisted([]);
      setPersistedLoaded(false);
      setAccountCreatedAt(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const [{ data: rows }, { data: profile }] = await Promise.all([
        supabase.from('user_achievements').select('badge_id, earned_at').eq('user_id', userId),
        supabase.from('profiles').select('created_at').eq('id', userId).maybeSingle(),
      ]);
      if (cancelled) return;
      setPersisted((rows ?? []).map(r => ({ badgeId: r.badge_id, earnedAt: r.earned_at })));
      setAccountCreatedAt(profile?.created_at ?? null);
      setPersistedLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const ctx = useMemo(() => {
    if (!loaded) return null;
    const completions = store.getCompletions();
    const milestones = store.getMilestones();
    const bands = store.getBands();
    const child = store.getChild();
    const notesCount = Object.values((store as any).getCompletions ? {} : {}).length;
    // We can't reach the private notes map directly; count via store helper:
    const allNotes = milestones.reduce((sum, m) => sum + (store.getNote(m.id).trim() ? 1 : 0), 0);
    return {
      completions,
      milestones,
      bands,
      notesCount: allNotes,
      streakDays: store.streakDays(),
      childBirthdate: child?.birthdate ?? null,
      accountCreatedAt,
    };
  }, [loaded, store, accountCreatedAt]);

  const earnedNow = useMemo(() => {
    if (!ctx) return new Set<string>();
    return evaluateBadges(ctx);
  }, [ctx]);

  // Toast newly-earned badges and refetch persisted rows from the server.
  // Badges are written by a server-side trigger on completions/notes/streak/
  // child changes — clients can no longer insert into user_achievements, so
  // we just listen for diffs and pull the authoritative list.
  useEffect(() => {
    if (!userId || !ctx || !persistedLoaded) return;
    const persistedSet = new Set(persisted.map(p => p.badgeId));
    const newlyEarned = [...earnedNow].filter(id => !persistedSet.has(id));
    if (newlyEarned.length === 0) return;

    // Toast immediately based on local eval (server agrees on the rules).
    for (const id of newlyEarned) {
      if (id === 'pioneer') continue;
      const badge = BADGES.find(b => b.id === id);
      if (!badge) continue;
      toast.success(`${badge.emoji} Badge unlocked: ${badge.title}`);
      track('beta_feedback_clicked', { kind: 'badge_unlocked', badge_id: id });
    }

    // Refetch persisted rows shortly after so server-written badges show up.
    const timer = setTimeout(async () => {
      const { data: refreshed } = await supabase
        .from('user_achievements')
        .select('badge_id, earned_at')
        .eq('user_id', userId);
      if (refreshed) {
        setPersisted(refreshed.map(r => ({ badgeId: r.badge_id, earnedAt: r.earned_at })));
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [earnedNow, persisted, persistedLoaded, userId, ctx]);

  return {
    earnedSet: earnedNow,
    persisted,
    earnedCount: earnedNow.size,
    totalCount: BADGES.length,
  };
}
